import { type ReactNode, type Ref } from 'react'
import cx from 'classnames'
import styles from './Button.module.css'
import { Spinner } from '@/components/Spinner'
import { type ElementState } from '@/infra/prop-types'
import { type PressEventHandler } from '@/infra/prop-types'
import {
  Button as AriaButton,
  type ButtonRenderProps as AriaButtonRenderProps,
} from 'react-aria-components'
import { Icon, type IconName } from '@/components/Icon'
import { RawLink, type LinkRenderProps } from '@/components/Link'
import { type To } from 'react-router'
import { Skeleton, useIsSkeleton } from '@/components/Skeleton'

/**
 * Variants of buttons.
 */
export type ButtonVariant =
  | 'default'
  | 'outline'
  | 'text'
  | 'text-destructive'
  | 'text-inline'
  | 'text-inline-navigation'
  | 'destructive'

export type ButtonRenderProps = AriaButtonRenderProps

export interface ButtonProps {
  /**
   * The button's label.
   */
  label: string

  /**
   * Button type.
   */
  variant?: ButtonVariant

  /**
   * Press handler.
   */
  onPress?: PressEventHandler

  /**
   * Whether the button is disabled.
   */
  isDisabled?: boolean

  /**
   * Display a loading indicator instead of the text and disables interactions.
   */
  isPending?: boolean

  /**
   * Allows emulating a state, for example when a button is not receiving
   * pointer events due to being behind a file input.
   */
  state?: ElementState

  /**
   * Ref to the underlying button.
   */
  ref?: Ref<HTMLButtonElement | HTMLAnchorElement | HTMLSpanElement>

  /**
   * Button type.
   */
  type?: 'submit' | 'button' | 'reset'

  /**
   * When the {@link type} is `submit`, this attribute can refer to the
   * form that the button belongs to without being nested within the form.
   */
  form?: string

  /**
   * Optional icon for the left side of the label.
   */
  iconLeft?: IconName

  /**
   * Whether the button should render as a skeleton.
   */
  isSkeleton?: boolean

  /**
   * If specified, used for receiving props based on the React Aria slot.
   */
  slot?: string

  /**
   * Router link destination.
   */
  to?: To

  /**
   * External link URL.
   */
  href?: string

  /**
   * Link target attribute.
   */
  target?: string

  /**
   * When used with `to`, causes the route to be replaced rather than pushed.
   */
  replace?: boolean

  /**
   * For links, if `true`, prevents resetting the scroll position.
   */
  preventScrollReset?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Buttons are used to initialize an action. Button labels express what
 * action will occur when the user interacts with it.
 */
export function Button({
  label,
  isDisabled,
  isPending,
  onPress,
  state,
  ref,
  form,
  iconLeft,
  isSkeleton,
  slot,
  type = 'button',
  variant = 'default',
  to,
  href,
  target,
  replace,
  preventScrollReset,
  testId,
}: ButtonProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  isDisabled ||= isSkeleton

  return (
    <RawButton
      form={form}
      onPress={onPress}
      isDisabled={isDisabled}
      ref={ref}
      type={type}
      state={state}
      isPending={isPending}
      slot={slot}
      to={to}
      href={href}
      target={target}
      replace={replace}
      preventScrollReset={preventScrollReset}
      testId={testId}
      className={({
        isFocused,
        isHovered,
        isPressed,
        isPending,
        isDisabled,
        isFocusVisible,
      }) =>
        cx(
          styles.button,
          styles[variant],
          isPending && styles.loading,
          isFocused && styles.focus,
          isHovered && styles.hover,
          isPressed && styles.pressed,
          isDisabled && styles.disabled,
          isFocusVisible && styles.focusVisible,
          isSkeleton && styles.skeleton,
        )
      }
    >
      {isSkeleton ? (
        <>
          {iconLeft && (
            <span className={styles.iconLeft}>
              <Icon sm skeleton name={iconLeft} />
            </span>
          )}
          <span className={styles.label}>
            <Skeleton disableCorrections block>
              {label}
            </Skeleton>
          </span>
        </>
      ) : (
        <>
          {iconLeft && (
            <span className={styles.iconLeft}>
              <Icon sm name={iconLeft} />
            </span>
          )}

          <span className={styles.label}>{label}</span>
          <span className={styles.spinner}>
            <Spinner variant="inherit" />
          </span>
        </>
      )}
    </RawButton>
  )
}

interface RawButtonProps {
  children?: ReactNode | ((props: ButtonRenderProps) => ReactNode)

  /**
   * @see {HTMLElement.inert}
   */
  inert?: boolean

  /**
   * Class name to apply.
   */
  className?: string | ((props: ButtonRenderProps) => string)

  /**
   * Press handler.
   */
  onPress?: PressEventHandler

  /**
   * Whether the button is disabled.
   */
  isDisabled?: boolean

  /**
   * Whether the button's action is currently pending.
   */
  isPending?: boolean

  /**
   * Allows emulating a state, for example when a button is not receiving
   * pointer events due to being behind a file input.
   */
  state?: ElementState

  /**
   * Ref to the underlying button.
   */
  ref?: Ref<HTMLButtonElement | HTMLAnchorElement | HTMLSpanElement>

  /**
   * Button type.
   */
  type?: 'submit' | 'button' | 'reset'

  /**
   * When the {@link type} is `submit`, this attribute can refer to the
   * form that the button belongs to without being nested within the form.
   */
  form?: string

  /**
   * If specified, used for receiving props based on the React Aria slot.
   */
  slot?: string

  /**
   * Router link destination.
   */
  to?: To

  /**
   * External link URL.
   */
  href?: string

  /**
   * Link target attribute.
   */
  target?: string

  /**
   * When used with `to`, causes the route to be replaced rather than pushed.
   */
  replace?: boolean

  /**
   * For links, if `true`, prevents resetting the scroll position.
   */
  preventScrollReset?: boolean

  /**
   * Accessibility label.
   */
  'aria-label'?: string

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * A button component that removes all default styling.
 */
export function RawButton({
  className,
  children,
  isDisabled,
  isPending,
  onPress,
  state,
  ref,
  form,
  type = 'button',
  slot,
  to,
  href,
  target,
  replace,
  preventScrollReset,
  testId,
  ...rest
}: RawButtonProps) {
  if (to || href) {
    const mapToButtonProps = (props: LinkRenderProps): ButtonRenderProps => ({
      isFocused: props.isFocused,
      isHovered: props.isHovered,
      isPressed: props.isPressed,
      isDisabled: props.isDisabled,
      isFocusVisible: props.isFocusVisible,
      isPending: isPending ?? false,
    })

    return (
      <RawLink
        {...(to ? { to } : {})}
        {...(href ? { href } : {})}
        target={target}
        replace={replace}
        preventScrollReset={preventScrollReset}
        onPress={onPress}
        isDisabled={isDisabled}
        ref={ref as Ref<HTMLAnchorElement | HTMLSpanElement>}
        testId={testId}
        className={(props: LinkRenderProps) => {
          const buttonProps = mapToButtonProps(props)
          return cx(
            styles.raw,
            getButtonClassName(className, buttonProps, state),
          )
        }}
        {...rest}
      >
        {(props: LinkRenderProps) => {
          const buttonProps = mapToButtonProps(props)
          return typeof children === 'function'
            ? children(buttonProps)
            : children
        }}
      </RawLink>
    )
  }

  return (
    <AriaButton
      form={form}
      onPress={onPress}
      isDisabled={isDisabled}
      isPending={isPending}
      ref={ref as Ref<HTMLButtonElement>}
      type={type}
      slot={slot}
      data-test={testId}
      className={(props) =>
        cx(styles.raw, getButtonClassName(className, props, state))
      }
      {...rest}
    >
      {children}
    </AriaButton>
  )
}

function getButtonClassName(
  className: string | ((props: AriaButtonRenderProps) => string) | undefined,
  {
    isFocused,
    isHovered,
    isPressed,
    isDisabled,
    isFocusVisible,
    isPending,
  }: AriaButtonRenderProps,
  state: ElementState | undefined,
): string | undefined {
  return typeof className === 'function'
    ? className({
        isFocused: isFocused || state === 'focus',
        isHovered: isHovered || state === 'hover',
        isPressed: isPressed || state === 'press',
        isDisabled: isDisabled,
        isFocusVisible: isFocusVisible || state === 'focus-visible',
        isPending: isPending,
      })
    : className
}
